/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import ReprocessingOverlay from '../../pages/Schedule/ReprocessingOverlay'
import { Flex } from '../Layout'
import List from '../List'
import GameListFilters from '../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../List/useGetScheduleFiltering'
import Picture from '../Picture'
import { media } from '../Responsive'

const stats = [
  {
    text: 'GP',
    toolTip: 'Games Played',
  },
  {
    text: 'W',
    toolTip: 'Wins',
  },
  {
    text: 'L',
    toolTip: 'Losses',
  },
  {
    text: 'T',
    toolTip: 'Ties',
  },
  {
    text: 'OTL',
    toolTip: 'Overtime Losses',
  },
  {
    text: 'SOL',
    toolTip: 'Shootout Losses',
  },
  {
    text: 'PTS',
    toolTip: 'Points',
  },
  {
    text: 'WPCT',
    toolTip: 'Winning Percentage',
  },
  {
    text: 'WPCTD',
    toolTip: 'In-Division Winning Percentage',
  },
  {
    text: 'GF',
    toolTip: 'Goals For',
  },
  {
    text: 'GA',
    toolTip: 'Goals Against',
  },
  {
    text: 'GPCT',
    toolTip: 'Game Percentage',
  },
  {
    text: 'PiM',
    toolTip: 'Penalty Minutes',
  },
  {
    accessor: 'OTW',
    text: 'ROW',
    toolTip: 'Overtime Wins',
  },
]

const Standings = ({
  areFiltersHidden,
  setIsFilterToggleDisabled = () => {},
  id,
  defaultSort,
  refreshKey,
  scheduleReprocessingListener,
  statWhitelist = [],
  shouldStickyHeader = true,
  hideGroupName = false,
}) => {
  const { filters, state, setState, loaded } = useGetScheduleFiltering(
    { schedule_id: id },
    { defaultSort: 'desc' }
  )

  const [scheduleId, setScheduleId] = useState(id)
  const [grouping, setGrouping] = useState(filters?.[filters?.length - 1]?.name)
  const [ready, setReady] = useState(false)

  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState(defaultSort)
  const [direction, setDirection] = useState('desc')
  const [items, setItems] = useState([])
  const [groups, setGroups] = useState([])

  useEffect(() => {
    return () => {
      setIsFilterToggleDisabled(false)
    }
  }, [])

  useEffect(() => {
    // Wait for schedule filters to finish loading, then set the grouping and load statistics
    if (loaded) {
      setGrouping(filters?.[filters?.length - 1]?.name || '')
      setReady(true)
      if (filters?.length === 0) {
        setIsFilterToggleDisabled(true)
      }
    }
  }, [loaded, filters?.length])

  const getItems = async (page) => {
    setLoading(true)
    let groupBy
    if (grouping) groupBy = grouping

    const slug = `/schedules/${scheduleId}/stats/byType`

    const response = await req(slug, {
      query: { groupBy, page },
    })

    const g = {}
    response.data.forEach((row) => {
      g[row.schedule.id] = g[row.schedule.id] || {}
      g[row.schedule.id].rows = g[row.schedule.id].rows || []
      g[row.schedule.id].name = row.schedule.name
      if (row?.team !== null) {
        g[row.schedule.id].rows.push(row)
      }
    })

    setItems(response.data)
    setGroups(g)
    setLoading(false)
  }

  useEffect(() => {
    ready && getItems()
  }, [ready, scheduleId, grouping, refreshKey])

  const headers = [
    {
      text: 'Team',
      sizePercent: 34,
      sort: 'team.name',
      minWidth: 200,
      Cell: ({ item, isSorted }) => {
        let link
        if (item?.team?.id) {
          link = `${ROUTES.TEAM_ROOT}/${item.team.id}?schedule_id=${id}`
        }

        return (
          <Flex
            alignItems='center'
            as={link ? Link : undefined}
            to={link ? link : undefined}
            css={css`
              font-size: 14px;
              padding-right: 8px;
              ${isSorted && `color: ${colors.DEFAULT_FLAIR};`}
            `}
          >
            <Picture
              square
              fit
              size='xxsmall'
              imageId={getImageThumbnailId(item?.team)}
              iconName='user-friends'
              css={css`
                margin-right: 8px;
              `}
            />
            {item?.team?.name_full}
          </Flex>
        )
      },
    },
    ...stats
      .filter((s) => {
        if (statWhitelist.length > 0) {
          return statWhitelist.includes(s.text)
        }
        return true
      })
      .map((stat) => {
        const accessor = stat.accessor || stat.text
        return {
          text: stat.text,
          sizePercent: 11,
          sort: accessor,
          fontSize: 14,
          minWidth: 54,
          toolTip: stat.toolTip,
          accessor: (item) => {
            return `${item.stats.find((s) => s.abbr === accessor)?.value || 0}`
          },
          Header: () => {
            return (
              <div
                css={css`
                  .mobile {
                    display: none;
                  }

                  ${media.mobile} {
                    .desktop {
                      display: none;
                    }

                    .mobile {
                      display: block;
                    }
                  }
                `}
              >
                <div className='desktop'>{stat.bigText || stat.text}</div>
                <div className='mobile'>{stat.text}</div>
              </div>
            )
          },
        }
      }),
  ]

  return (
    <>
      <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      />
      <div
        css={css`
          margin-top: 24px;

          ${scheduleReprocessingListener &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        <GameListFilters
          filters={filters}
          state={state}
          setState={setState}
          loading={loading}
          onScheduleChange={(scheduleId, scheduleName) => {
            if (scheduleId) {
              setScheduleId(scheduleId)
              setGrouping(scheduleName)
            } else {
              setScheduleId(id)
              setGrouping(filters?.[filters?.length - 1]?.name || '')
            }
          }}
          isHidden={areFiltersHidden}
        />
        {ready && (
          <List
            headers={headers.filter((h) => typeof h !== 'undefined')}
            fullNoItemsText='There are no standings available yet'
            shouldStickyHeader={shouldStickyHeader}
            highlightSorted
            localSort={(sortBy, direction, items) => {
              return items.sort((a, b) => {
                const aValue = a?.stats?.find((s) => s?.abbr === sortBy)?.value
                const aFactor = !['WPCT', 'WPCTD', 'GPCT'].includes(sortBy)
                  ? parseInt(aValue, 10)
                  : aValue

                const bValue = b?.stats?.find((s) => s?.abbr === sortBy)?.value
                const bFactor = !['WPCT', 'WPCTD', 'GPCT'].includes(sortBy)
                  ? parseInt(bValue, 10)
                  : bValue

                if (aFactor < bFactor) {
                  return direction === 'asc' ? -1 : 1
                } else if (aFactor > bFactor) {
                  return direction === 'desc' ? -1 : 1
                }

                return 0
              })
            }}
            listProps={{
              items,
              groups,
              loading,
              setDirection,
              setSort: setSortBy,
              sort: sortBy,
              direction,
              getItems,
              getKey: (item) => `${item?.team?.id}-${item?.schedule?.id}`,
            }}
            hideGroupName={hideGroupName}
          />
        )}
      </div>
    </>
  )
}

export default Standings
